/**
 * Pure shaping for the applicant-history panel — every rule in
 * frontend_documentation/applicant-history.md "Rendering" lives here so the
 * card section and the Add Applicant lookup render identically.
 *
 * Colour rule from the doc: the panel is INFORMATION, not a warning. Only two
 * things are coloured — amber for 30+ days delinquent, red for foreclosure /
 * REO. Everything else is neutral or grey.
 */
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import {
  daysUntilDateOnly,
  formatDate,
} from "../../../_src_shared/utils/functions/formatDate";
import {
  formatDateTime,
  timestampMs,
} from "../../../_src_shared/utils/functions/formatDateTime";

/** Tone keys → the panel maps them onto `status` tokens. */
export const TONE = {
  neutral: "neutral",
  good: "good",
  amber: "amber",
  red: "red",
  grey: "grey",
};

const FORECLOSURE_LABELS = ["FORECLOSURE", "PRE FORECLOSURE", "REO"];

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const plural = (count, one, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

export const fmtMoney = (v) =>
  v == null || !Number.isFinite(Number(v))
    ? "—"
    : formatDollar(Math.round(Number(v)), "—");

/**
 * The collapsed one-liner:
 *   `{matchedLoans} prior loans · {paidOff} paid off · {current} current ·
 *    {delinquent} late · ${outstandingBalance} outstanding`
 * — non-zero parts only; amber "N delinquent 30+ days", red "N in foreclosure
 * / REO"; grey "+N possible (name only)". When FCI was unavailable every FCI
 * count is null, so only the loan count shows plus "FCI unavailable".
 */
export const summaryParts = (summary, { fciAvailable = true } = {}) => {
  const s = summary || {};
  const matched = n(s.matchedLoans);
  const possible = n(s.possibleLoans);
  if (matched === 0 && possible === 0) {
    return [{ text: "No other EV or LQ loans found.", tone: TONE.grey }];
  }
  const parts = [];
  if (matched > 0)
    parts.push({ text: plural(matched, "prior loan"), tone: TONE.neutral });
  if (!fciAvailable) {
    parts.push({ text: "FCI unavailable", tone: TONE.grey });
  } else {
    if (n(s.paidOff) > 0)
      parts.push({ text: `${s.paidOff} paid off`, tone: TONE.neutral });
    if (n(s.current) > 0)
      parts.push({ text: `${s.current} current`, tone: TONE.neutral });
    if (n(s.delinquent) > 0)
      parts.push({ text: `${s.delinquent} late`, tone: TONE.neutral });
    if (n(s.outstandingBalance) > 0) {
      parts.push({
        text: `${fmtMoney(s.outstandingBalance)} outstanding`,
        tone: TONE.neutral,
      });
    }
    if (n(s.delinquent30Plus) > 0) {
      parts.push({
        text: `${s.delinquent30Plus} delinquent 30+ days`,
        tone: TONE.amber,
      });
    }
    if (n(s.foreclosureOrReo) > 0) {
      parts.push({
        text: `${s.foreclosureOrReo} in foreclosure / REO`,
        tone: TONE.red,
      });
    }
  }
  if (possible > 0) {
    parts.push({ text: `+${possible} possible (name only)`, tone: TONE.grey });
  }
  return parts;
};

/**
 * FCI status per the doc's table. `label` is the doc's full wording (tooltip /
 * detail); `short` is the row pill. `fci === null` → "FCI unavailable".
 */
export const fciStatusLabel = (fci) => {
  if (!fci) return { label: "FCI unavailable", short: "FCI unavailable", tone: TONE.grey };
  const dpd = fci.daysPastDue;
  switch (fci.status) {
    case "CLEAN":
      return { label: "Current", short: "Current", tone: TONE.good };
    case "IN_GRACE":
      return {
        label: `Late, within grace${dpd != null ? ` (${plural(n(dpd), "day")})` : ""}`,
        short: "In grace",
        tone: TONE.neutral,
      };
    case "DELINQUENT_NOW": {
      if (fci.delinquencyBucket == null) {
        return {
          label: `Late ${plural(n(dpd), "day")} (past grace, under 30)`,
          short: `Late ${plural(n(dpd), "day")}`,
          tone: TONE.amber,
        };
      }
      const foreclosure = FORECLOSURE_LABELS.includes(
        String(fci.fciStatus || "").toUpperCase(),
      );
      return {
        label: `Delinquent ${fci.delinquencyBucket}+ days`,
        short: foreclosure ? "Foreclosure / REO" : `${fci.delinquencyBucket}+ days late`,
        tone: foreclosure ? TONE.red : TONE.amber,
      };
    }
    case "PAID_OFF":
      return { label: "Paid off", short: "Paid off", tone: TONE.neutral };
    case "NOT_BOARDED":
      return fci.servicedOutsideFci
        ? {
            label: "Serviced by Shellpoint (not in FCI)",
            short: "Shellpoint",
            tone: TONE.grey,
          }
        : { label: "Not in FCI", short: "Not boarded", tone: TONE.grey };
    default: {
      const label = fci.fciStatus || fci.status || "—";
      return { label, short: label, tone: TONE.neutral };
    }
  }
};

/** Boarded to FCI (FCI can report on it) — drives which detail fields show. */
export const isBoarded = (fci) =>
  Boolean(fci) && fci.status !== "NOT_BOARDED";

/** "1× 30-day late in the last 12 months" — only the non-zero buckets. */
export const lateHistoryText = (late12Months) => {
  if (!late12Months) return null;
  const bits = ["30", "60", "90"]
    .filter((k) => n(late12Months[k]) > 0)
    .map((k) => `${late12Months[k]}× ${k}-day late`);
  return bits.length ? `${bits.join(", ")} in the last 12 months` : null;
};

/** The "Late payments, 12 mo" cell: null → not reported, all zero → None. */
export const lateHistoryShort = (late12Months) => {
  if (!late12Months) return "Not reported";
  const bits = ["30", "60", "90"]
    .filter((k) => n(late12Months[k]) > 0)
    .map((k) => `${late12Months[k]}× ${k}-day`);
  return bits.length ? bits.join(", ") : "None";
};

/**
 * The applicant-level verdict banner. Information, not a warning: only 30+
 * days delinquent is amber and foreclosure / REO red (the doc's two colours);
 * green is "every boarded loan is current or paid off".
 */
export const verdict = (summary, { fciAvailable = true } = {}) => {
  const s = summary || {};
  const matched = n(s.matchedLoans);
  const possible = n(s.possibleLoans);
  const possibleNote =
    possible > 0 ? `+${possible} possible (name only) — not confirmed` : null;
  const join = (bits) => bits.filter(Boolean).join(" · ");

  if (matched === 0) {
    return {
      tone: TONE.neutral,
      title: possible > 0 ? "No confirmed prior loans" : "No other EV or LQ loans found",
      detail: possibleNote,
    };
  }
  if (!fciAvailable) {
    return {
      tone: TONE.grey,
      title: "Servicing status unavailable",
      detail: join([
        `${plural(matched, "prior loan")} found; FCI couldn't be searched`,
        possibleNote,
      ]),
    };
  }

  const counts = join([
    n(s.current) > 0 && `${s.current} current`,
    n(s.inGrace) > 0 && `${s.inGrace} in grace`,
    n(s.delinquent) > 0 && `${s.delinquent} late`,
    n(s.paidOff) > 0 && `${s.paidOff} paid off`,
    n(s.outstandingBalance) > 0 && `${fmtMoney(s.outstandingBalance)} outstanding`,
  ]);
  const unboarded = join([
    n(s.notBoarded) > 0 &&
      `${plural(n(s.notBoarded), "loan")} not boarded to FCI — not adverse`,
    n(s.servicedOutsideFci) > 0 &&
      `${n(s.servicedOutsideFci)} serviced by Shellpoint`,
  ]);

  if (n(s.foreclosureOrReo) > 0) {
    return {
      tone: TONE.red,
      title: `${plural(n(s.foreclosureOrReo), "loan")} in foreclosure / REO`,
      detail: join([counts, possibleNote]),
    };
  }
  if (n(s.delinquent30Plus) > 0) {
    return {
      tone: TONE.amber,
      title: `${plural(n(s.delinquent30Plus), "loan")} delinquent 30+ days`,
      detail: join([counts, possibleNote]),
    };
  }
  if (n(s.delinquent) > 0) {
    return {
      tone: TONE.neutral,
      title: "Currently late, under 30 days",
      detail: join([counts, possibleNote]),
    };
  }
  if (n(s.current) + n(s.inGrace) + n(s.paidOff) > 0) {
    return {
      tone: TONE.good,
      title: "Clean servicing history",
      detail: join([counts, unboarded, possibleNote]),
    };
  }
  return {
    tone: TONE.grey,
    title: "No servicing history yet",
    detail: join([unboarded, possibleNote]),
  };
};

/**
 * A loan's display title. LOS loan names are "EV29887 - 205 5th St,Belvidere,
 * NJ,07823 - RTL (Pre)": drop the id prefix and the product suffix, and put
 * the spaces back in the address ("205 5th St, Belvidere, NJ 07823").
 */
export const loanTitle = (row) => {
  const raw = String(row?.loanName || "").trim();
  if (!raw) return row?.loanId || (row?.loanNumber ? `#${row.loanNumber}` : "—");
  let parts = raw.split(" - ").map((p) => p.trim()).filter(Boolean);
  if (parts.length > 1 && row?.loanId && parts[0] === row.loanId) {
    parts = parts.slice(1);
  }
  if (parts.length > 1) parts = [parts[0]];
  const addr = parts[0]
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  // "…, NJ, 07823" → "…, NJ 07823"
  if (
    addr.length >= 2 &&
    /^\d{5}(-\d{4})?$/.test(addr[addr.length - 1]) &&
    /^[A-Z]{2}$/.test(addr[addr.length - 2])
  ) {
    const zip = addr.pop();
    addr[addr.length - 1] = `${addr[addr.length - 1]} ${zip}`;
  }
  return addr.join(", ");
};

/** The row's short reference: the LOS loan id, else "#loanNumber". */
export const loanRef = (row) =>
  row?.loanId || (row?.loanNumber != null ? `#${row.loanNumber}` : null);

/** Display-only: "Fix and Flip" → "Fix & Flip". */
export const productLabel = (v) =>
  v ? String(v).replace(/\s+and\s+/i, " & ") : null;

/** "Found in EV LOS, Salesforce". */
export const foundIn = (row) => {
  const bits = (row?.sources || []).map((src) =>
    src === "LOS"
      ? `${row?.originatingLos || ""} LOS`.trim()
      : src === "SF"
        ? "Salesforce"
        : src,
  );
  return bits.length ? `Found in ${bits.join(", ")}` : null;
};

/** A confident match that didn't use the SSN — worth a second look. */
export const matchedWithoutSsn = (row) =>
  row?.tier === "match" && !/\bSSN\b/.test(String(row?.basis || ""));

/** Where a loan is in its term: month N of M, % elapsed, days to maturity. */
export const termProgress = (fundedDate, maturityDate) => {
  const toMat = daysUntilDateOnly(maturityDate);
  const sinceFunded = daysUntilDateOnly(fundedDate);
  if (toMat == null || sinceFunded == null) return null;
  const elapsed = -sinceFunded;
  const total = elapsed + toMat;
  if (total <= 0 || elapsed < 0) return null;
  const DAYS_PER_MONTH = 365.25 / 12;
  const totalMonths = Math.max(1, Math.round(total / DAYS_PER_MONTH));
  const month = Math.min(
    totalMonths,
    Math.floor(elapsed / DAYS_PER_MONTH) + 1,
  );
  return {
    month,
    totalMonths,
    pct: Math.max(0, Math.min(1, elapsed / total)),
    daysToMaturity: toMat,
  };
};

export const fmtLongDate = (v) => (v ? formatDate(v, "—", "MMM d, yyyy") : "—");

/**
 * Where a row opens. Only `openable` rows link: underwriting → `/loan/{id}`,
 * acquisitions → `/acquisition/{id}` (singular). Other-LOS and `sf:` rows are
 * plain text with the LOS badge.
 */
export const loanHref = (row) => {
  if (!row?.openable || !row?.dealId || String(row.dealId).startsWith("sf:")) {
    return null;
  }
  if (row.pipeline === "acquisitions") return `/acquisition/${row.dealId}`;
  if (row.pipeline === "underwriting") return `/loan/${row.dealId}`;
  return null;
};

export const fmtFunded =(v) => (v ? formatDate(v, "—", "M/d/yyyy") : "—");

/** "Checked 3m ago" off `generatedAt` (a UTC timestamp). */
export const checkedAgo = (iso) => {
  const then = timestampMs(iso);
  if (then == null) return null;
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateTime(iso, "date");
};

export const checkedTooltip = (iso) =>
  iso ? formatDateTime(iso, "datetime") : "";

/**
 * Banner lines for a partial result: `notes[]` verbatim, plus the doc's fixed
 * sentence when FCI or Salesforce could not be searched.
 */
export const partialNotes = (payload) => {
  if (!payload) return [];
  const out = [];
  const searched = payload.searched || {};
  if (searched.fci === false) {
    out.push("FCI unavailable, servicing status not shown.");
  }
  if (searched.salesforce === false) {
    out.push("Salesforce unavailable, Salesforce-only loans not searched.");
  }
  for (const note of payload.notes || []) {
    if (note && !out.includes(note)) out.push(String(note));
  }
  return out;
};

/** Split a person's loans into confirmed matches and name-only possibles. */
export const splitLoans = (loans) => {
  const matches = [];
  const possible = [];
  for (const row of loans || []) {
    (row?.tier === "possible" ? possible : matches).push(row);
  }
  return { matches, possible };
};
